import { Input, Text, VStack } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { formatApiDate, nowSaoPauloDateTimeLocal, useAppToast } from '../../../helpers'
import { JobRequisitionService } from '../../../services/JobRequisition/JobRequisitionService'
import { JobRequisition } from '../../../services/JobRequisition/types'

interface IJobRequisitionCloseModalProps {
  isOpen: boolean
  onClose: () => void
  onClosed: () => void
  requisition: JobRequisition | null
}

/** "Fechar vaga": the closing date makes the RP Concluída and takes it off the site. */
export function JobRequisitionCloseModal({ isOpen, onClose, onClosed, requisition }: IJobRequisitionCloseModalProps) {
  const toast = useAppToast()
  // The API validates against São Paulo's date, whatever the browser's time zone.
  const today = nowSaoPauloDateTimeLocal().slice(0, 10)
  const [closedAt, setClosedAt] = useState(today)
  const [error, setError] = useState<string[]>()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setClosedAt(today)
    setError(undefined)
  }, [isOpen])

  const handleSave = () => {
    if (!requisition) return

    // Same rules as the API, with its messages, before the request.
    if (requisition.opened_at && closedAt < requisition.opened_at) {
      setError(['A data de fechamento não pode ser anterior à data de abertura.'])
      return
    }
    if (closedAt > today) {
      setError(['A data de fechamento não pode ser futura.'])
      return
    }

    setSaving(true)
    new JobRequisitionService()
      .close(requisition.id, closedAt)
      .then(() => {
        toast.success(`RP ${requisition.number} concluída`)
        onClosed()
        onClose()
      })
      .catch((err) => {
        const errors = err?.response?.data?.errors
        if (errors?.closed_at) {
          setError(errors.closed_at)
          return
        }
        toast.error('Erro ao fechar a RP', err)
      })
      .finally(() => setSaving(false))
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Fechar RP ${requisition?.number ?? ''}`}
      onSubmit={handleSave}
      size="compact"
      confirmLabel="Fechar vaga"
      isLoading={saving}
      isConfirmDisabled={!closedAt}
      // The error goes under the field, like the rest of the panel, not in the browser's bubble.
      noValidate
    >
      <VStack align="stretch" spacing={4}>
        <Text>A vaga fica Concluída e sai do site. O tempo de fechamento é calculado em dias úteis.</Text>
        <FormField
          label="Data de Fechamento"
          error={error}
          helperText={requisition?.opened_at ? `Aberta em ${formatApiDate(requisition.opened_at)}.` : undefined}
          isRequired
        >
          <Input
            type="date"
            value={closedAt}
            min={requisition?.opened_at ?? undefined}
            max={today}
            onChange={(e) => {
              setClosedAt(e.target.value)
              setError(undefined)
            }}
            isDisabled={saving}
          />
        </FormField>
      </VStack>
    </FormModal>
  )
}
